import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { respondToCalendarShareRequest } from './calendarSlice';
import { acceptConnection, rejectConnection } from './connectionSlice';
import { createAction } from '@reduxjs/toolkit';

// Define a flag to disable notification features - must match the one in other files
const NOTIFICATIONS_ENABLED = false; // Set to false to disable notifications

// Define notification types
export const NOTIFICATION_TYPES = {
  CONNECTION_REQUEST: 'CONNECTION_REQUEST',
  CONNECTION_ACCEPTED: 'CONNECTION_ACCEPTED',
  CONNECTION_REJECTED: 'CONNECTION_REJECTED',
  MESSAGE: 'MESSAGE',
  REMINDER: 'REMINDER',
  SYSTEM: 'SYSTEM',
  EVENT: 'EVENT',
  CALENDAR_SHARE_REQUEST: 'CALENDAR_SHARE_REQUEST',
  CALENDAR_SHARE_ACCEPTED: 'CALENDAR_SHARE_ACCEPTED',
  CALENDAR_SHARE_REJECTED: 'CALENDAR_SHARE_REJECTED'
};

// Define notification status
export const NOTIFICATION_STATUS = {
  UNREAD: 'UNREAD',
  READ: 'READ',
  ARCHIVED: 'ARCHIVED'
};

// Async thunk to fetch notifications
export const fetchNotifications = createAsyncThunk(
  'notifications/fetchNotifications',
  async (userId, { rejectWithValue }) => {
    if (!NOTIFICATIONS_ENABLED) {
      console.log('Notifications are disabled, skipping fetch');
      return [];
    }
    
    try {
      // Try to use the socket first
      if (window.socketInstance && window.socketInstance.readyState === WebSocket.OPEN) {
        // Implementation for socket fetch - actual logic is in socket event handlers
        console.log('Using socket to fetch notifications');
        window.socketInstance.send(JSON.stringify({
          action: 'fetch_notifications',
          userId: userId
        }));
        // Socket will handle the response asynchronously
        return []; // Return empty array for now until socket updates state
      }
      
      return []; // Return empty array if socket is not available
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      return rejectWithValue('Failed to fetch notifications');
    }
  }
);

// Async thunk to mark notification as read
export const markNotificationAsRead = createAsyncThunk(
  'notifications/markAsRead',
  async ({ notificationId, userId }, { rejectWithValue }) => {
    try {
      // This would be replaced with an actual API call
      // For now, we'll just return the notification ID
      return { notificationId };
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const respondToNotification = createAsyncThunk(
  'notifications/respondToNotification',
  async ({ notification, action }, { dispatch }) => {
    if (!NOTIFICATIONS_ENABLED) {
      console.log('Notifications are disabled, skipping response');
      return { notification, action };
    }
    
    try {
      if (notification.type === NOTIFICATION_TYPES.CONNECTION_REQUEST) {
        if (action === 'accept') {
          await dispatch(
            acceptConnection({
              requestId: notification.id,
              userId: notification.receiverId,
            })
          );
        } else if (action === 'reject') {
          await dispatch(
            rejectConnection({
              requestId: notification.id,
              userId: notification.receiverId,
            })
          );
        }
      } else if (notification.type === NOTIFICATION_TYPES.CALENDAR_SHARE_REQUEST) {
        await dispatch(
          respondToCalendarShareRequest({
            requestId: notification.id,
            action,
            userId: notification.receiverId,
          })
        );
      }
      
      // Mark notification as viewed after responding
      dispatch(markNotificationViewed(notification.id));
      
      return { notification, action };
    } catch (error) {
      throw new Error(`Failed to respond to notification: ${error.message}`);
    }
  }
);

// Initial state
const initialState = {
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  error: null
};

// Create the notification slice
const notificationSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    // Add a new notification
    addNotification: (state, action) => {
      try {
        // Log the incoming notification for debugging
        console.log('notificationSlice: Adding new notification:', action.payload);
        
        // Detect Firefox browser for special handling
        const isFirefox = typeof navigator !== 'undefined' && 
                         navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
        
        if (isFirefox) {
          console.log('notificationSlice: Firefox browser detected, applying special handling');
        }
        
        // Extract message with enhanced fallbacks
        let message = '';
        let senderName = '';
        
        // Extract sender information first - needed for multiple notification types
        if (action.payload.sender && action.payload.sender.name) {
          senderName = action.payload.sender.name;
        } else if (action.payload.data && action.payload.data.senderName) {
          senderName = action.payload.data.senderName;
        } else if (action.payload.senderName) {
          senderName = action.payload.senderName;
        } else if (action.payload.data && action.payload.data.sender && action.payload.data.sender.name) {
          senderName = action.payload.data.sender.name;
        } else if (action.payload.data && action.payload.data.requester && action.payload.data.requester.name) {
          senderName = action.payload.data.requester.name;
        } else if (action.payload.data && action.payload.data.requesterName) {
          senderName = action.payload.data.requesterName;
        } else {
          senderName = 'Someone';
        }
        
        console.log('notificationSlice: Extracted sender name:', senderName);
        
        // Type-specific formatting
        if (action.payload.type === 'CONNECTION_REQUEST' || 
            (action.payload.type && action.payload.type.toUpperCase() === 'CONNECTION_REQUEST')) {
          
          message = `${senderName} wants to connect with you`;
          console.log('notificationSlice: Created connection request message:', message);
          
          // Ensure we update the sender object if missing
          if (!action.payload.sender) {
            action.payload.sender = {
              name: senderName,
              id: action.payload.data?.senderId || action.payload.data?.requesterId || action.payload.senderId || null
            };
            console.log('notificationSlice: Created missing sender object for CONNECTION_REQUEST');
          }
        }
        // Normal message extraction for other notification types
        else if (action.payload.message) {
          message = action.payload.message;
        } else if (action.payload.data && action.payload.data.message) {
          message = action.payload.data.message;
        } else if (action.payload.type === 'CALENDAR_SHARE_REQUEST' && action.payload.data) {
          const eventTitle = action.payload.data.eventTitle || 
                            (action.payload.data.event && action.payload.data.event.title);
          if (eventTitle) {
            message = `You have been invited to "${eventTitle}"`;
          } else {
            message = 'You have been invited to a calendar event';
          }
        } else {
          // Default fallback
          message = 'You have a new notification';
        }
        
        // Generate complete notification object
        const notification = {
          id: action.payload.id || `notification-${Date.now()}`,
          type: action.payload.type,
          title: action.payload.type === 'CONNECTION_REQUEST' ? 'New Connection Request' : (action.payload.title || 'New Notification'),
          message: message,
          data: action.payload.data || {},
          status: NOTIFICATION_STATUS.UNREAD,
          timestamp: action.payload.timestamp || new Date().toISOString(),
          sender: action.payload.sender || {
            name: senderName,
            id: action.payload.data?.senderId || action.payload.senderId || null
          },
          viewed: false
        };
        
        // For Firefox, ensure data is properly structured
        if (isFirefox) {
          // Make sure data contains message for Firefox
          if (notification.data && !notification.data.message) {
            notification.data.message = notification.message;
          }
          
          // For Firefox, also ensure sender name is in data
          if (notification.data && !notification.data.senderName) {
            notification.data.senderName = senderName;
          }
        }
        
        // Log the final formatted notification being added
        console.log('notificationSlice: Formatted notification:', notification);
        
        // Enhanced duplicate detection
        let isDuplicate = false;
        
        // Check for exact ID duplicate
        if (state.notifications.some(n => n.id === notification.id)) {
          console.log('notificationSlice: Exact duplicate ID detected, not adding:', notification.id);
          isDuplicate = true;
        }
        
        // Check for very similar notifications that arrived within a short time
        if (!isDuplicate) {
          // Look for notifications with same type and similar message in the last 5 minutes
          const now = new Date();
          const recentNotifications = state.notifications.filter(n => {
            // Check if notification is recent (within last 5 minutes)
            const notifTime = new Date(n.timestamp);
            const timeDiff = now - notifTime; // difference in milliseconds
            return timeDiff < 5 * 60 * 1000; // 5 minutes
          });
          
          // Check if we have a similar notification in recent ones
          const similarNotification = recentNotifications.find(n => {
            // Match by type
            const sameType = n.type === notification.type;
            
            // Check for similar messages
            let similarMessage = false;
            if (n.message && notification.message) {
              // Either exact match or one contains the other (for differently formatted but similar messages)
              similarMessage = n.message === notification.message || 
                              n.message.includes(notification.message) ||
                              notification.message.includes(n.message);
            }
            
            // For event share requests, also check event title
            let sameEvent = false;
            if (notification.type.includes('EVENT') || notification.type.includes('CALENDAR')) {
              if (n.data && notification.data) {
                sameEvent = n.data.eventTitle === notification.data.eventTitle && 
                            n.data.eventTitle !== undefined && 
                            notification.data.eventTitle !== undefined;
              }
            }
            
            return sameType && (similarMessage || sameEvent);
          });
          
          if (similarNotification) {
            console.log('notificationSlice: Similar notification found, not adding duplicate:',
                       similarNotification.id);
            isDuplicate = true;
          }
        }
        
        // Only add if not a duplicate
        if (!isDuplicate) {
          // Add to the beginning of the array
          state.notifications.unshift(notification);
          state.unreadCount += 1;
          console.log(`notificationSlice: Added notification. New count: ${state.unreadCount}`);
        }
      } catch (error) {
        console.error('notificationSlice: Error adding notification:', error);
      }
    },
    
    // Mark a notification as read
    markAsRead: (state, action) => {
      const notification = state.notifications.find(n => n.id === action.payload);
      if (notification && notification.status === NOTIFICATION_STATUS.UNREAD) {
        notification.status = NOTIFICATION_STATUS.READ;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },
    
    // Mark all notifications as read
    markAllAsRead: (state) => {
      state.notifications.forEach(notification => {
        if (notification.status === NOTIFICATION_STATUS.UNREAD) {
          notification.status = NOTIFICATION_STATUS.READ;
        }
      });
      state.unreadCount = 0;
    },
    
    // Remove a notification
    removeNotification: (state, action) => {
      const index = state.notifications.findIndex(n => n.id === action.payload);
      if (index !== -1) {
        const notification = state.notifications[index];
        if (notification.status === NOTIFICATION_STATUS.UNREAD) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
        state.notifications.splice(index, 1);
      }
    },
    
    // Clear all notifications
    clearNotifications: (state) => {
      state.notifications = [];
      state.unreadCount = 0;
    },
    
    // Add a connection request notification
    addConnectionRequestNotification: (state, action) => {
      console.log('notificationSlice: Adding connection request notification:', action.payload);
      
      try {
        // Extract request data - handle different possible formats
        const requestData = action.payload.data || action.payload.request || action.payload;
        
        if (!requestData) {
          console.error('notificationSlice: Invalid request data received');
          return;
        }
        
        // Extract request ID with fallbacks
        const requestId = requestData.id || requestData._id || requestData.requestId || 
                        (requestData.request && (requestData.request.id || requestData.request._id));
        
        if (!requestId) {
          console.error('notificationSlice: Request has no ID:', requestData);
          return;
        }
        
        // Create a notification ID
        const notificationId = `connection-request-${requestId}`;
        
        // Enhanced duplicate detection: check for both ID and sender-based duplicates
        const isDuplicate = state.notifications.some(n => {
          // Check for exact ID match
          if (n.id === notificationId) {
            console.log('notificationSlice: Exact ID match found for connection request, not adding duplicate');
            return true;
          }
          
          // Check for matching request ID in data
          if (n.data && n.data.id === requestId) {
            console.log('notificationSlice: Matching request ID found in data, not adding duplicate');
            return true;
          }
          
          // Check for matching sender in CONNECTION_REQUEST type
          if (n.type === NOTIFICATION_TYPES.CONNECTION_REQUEST) {
            // Get the sender ID from this notification to compare
            const existingSenderId = n.sender?.id || 
                                    n.data?.senderId || 
                                    n.data?.requesterId || 
                                    (n.data?.requester && n.data.requester.id);
                                    
            // Get the sender ID from the new notification
            const newSenderId = requestData.senderId || 
                               requestData.requesterId || 
                               (requestData.requester && requestData.requester.id) || 
                               (requestData.sender && requestData.sender.id);
                               
            // If we have both IDs and they match, it's probably a duplicate
            if (existingSenderId && newSenderId && existingSenderId === newSenderId) {
              console.log('notificationSlice: Matching sender ID found, not adding duplicate connection request');
              return true;
            }
            
            // If the type and similar date, it's probably a duplicate
            const newTimestamp = new Date(requestData.createdAt || requestData.timestamp || new Date().toISOString());
            const existingTimestamp = new Date(n.timestamp);
            const timeDiff = Math.abs(newTimestamp - existingTimestamp);
            
            // If timestamps are within 10 seconds of each other, consider it a duplicate
            if (timeDiff < 10000) { // 10 seconds
              console.log('notificationSlice: Similar timestamp found for connection request, not adding duplicate');
              return true;
            }
          }
          
          return false;
        });
        
        if (isDuplicate) {
          console.log('notificationSlice: Connection request notification is a duplicate, not adding');
          return;
        }
        
        // Enhanced extraction of requester information with multiple fallbacks
        let requesterName = '';
        let requesterId = null;
        
        // Extract from requester object
        if (requestData.requester) {
          if (requestData.requester.name) requesterName = requestData.requester.name;
          else if (requestData.requester.username) requesterName = requestData.requester.username;
          else if (requestData.requester.firstName && requestData.requester.lastName) 
            requesterName = `${requestData.requester.firstName} ${requestData.requester.lastName}`.trim();
          else if (requestData.requester.firstName) requesterName = requestData.requester.firstName;
          
          requesterId = requestData.requester.id || requestData.requester._id;
        }
        
        // Try other fields if we don't have a name yet
        if (!requesterName) {
          requesterName = requestData.requesterName || 
                         requestData.senderName || 
                         requestData.userName || 
                         requestData.username ||
                         requestData.sender?.name ||
                         'Someone';
        }
        
        // Try other fields if we don't have an ID yet
        if (!requesterId) {
          requesterId = requestData.requesterId || 
                       requestData.senderId || 
                       requestData.userId ||
                       requestData.sender?.id;
        }
        
        console.log('notificationSlice: Creating notification for request from:', requesterName);
        
        // Create rich sender data object
        const senderData = {
          id: requesterId,
          name: requesterName,
          profile_photo_url: (requestData.requester && requestData.requester.profile_photo_url) || 
                            requestData.profile_photo_url || null
        };
        
        // Create notification object with descriptive title and clear message
        const notification = {
          id: notificationId,
          type: NOTIFICATION_TYPES.CONNECTION_REQUEST,
          title: 'New Connection Request',
          message: `${requesterName} wants to connect with you`,
          data: requestData,
          status: NOTIFICATION_STATUS.UNREAD,
          timestamp: requestData.createdAt || requestData.timestamp || new Date().toISOString(),
          viewed: false,
          sender: senderData
        };
        
        // Add to notifications array
        state.notifications.unshift(notification);
        
        // Increment unread count
        state.unreadCount += 1;
        
        console.log('notificationSlice: Added new connection request notification', notification);
      } catch (error) {
        console.error('notificationSlice: Error creating connection request notification:', error);
      }
    },
    
    // Add a connection accepted notification
    addConnectionAcceptedNotification: (state, action) => {
      console.log("addConnectionAcceptedNotification called");
      
      try {
        const { data } = action.payload;
        
        // Ensure we have valid data
        if (!data) {
          console.error('No data provided for connection accepted notification');
          return;
        }
        
        // Get the request ID
        const requestId = data.requestId;
        if (!requestId) {
          console.error('Request has no ID:', data);
          return;
        }
        
        // Create a notification ID
        const notificationId = `connection-accepted-${requestId}-${Date.now()}`;
        
        // Extract sender information
        const senderName = data.senderName || 'Someone';
        const senderData = {
          id: data.senderId,
          name: senderName,
          avatar: data.senderAvatar,
          profile_photo_url: data.senderAvatar
        };
        
        console.log('Creating new connection accepted notification from:', senderName);
        
        // Create the notification object
        const notification = {
          id: notificationId,
          type: NOTIFICATION_TYPES.CONNECTION_ACCEPTED,
          title: 'Connection Request Accepted',
          message: `${senderName} accepted your connection request`,
          data: data,
          status: NOTIFICATION_STATUS.UNREAD,
          timestamp: new Date().toISOString(),
          sender: senderData
        };
        
        // Add to state
        state.notifications.unshift(notification);
        state.unreadCount += 1;
        
        console.log('Notification added, new count:', state.unreadCount);
      } catch (error) {
        console.error('Error creating connection accepted notification:', error);
      }
    },
    
    // Add a connection rejected notification
    addConnectionRejectedNotification: (state, action) => {
      console.log("addConnectionRejectedNotification called");
      
      try {
        const { data } = action.payload;
        
        // Ensure we have valid data
        if (!data) {
          console.error('No data provided for connection rejected notification');
          return;
        }
        
        // Get the request ID
        const requestId = data.requestId;
        if (!requestId) {
          console.error('Request has no ID:', data);
          return;
        }
        
        // Create a notification ID
        const notificationId = `connection-rejected-${requestId}-${Date.now()}`;
        
        // Extract sender information
        const senderName = data.senderName || 'Someone';
        const senderData = {
          id: data.senderId,
          name: senderName,
          avatar: data.senderAvatar,
          profile_photo_url: data.senderAvatar
        };
        
        console.log('Creating new connection rejected notification from:', senderName);
        
        // Create the notification object
        const notification = {
          id: notificationId,
          type: NOTIFICATION_TYPES.CONNECTION_REJECTED,
          title: 'Connection Request Rejected',
          message: `${senderName} declined your connection request`,
          data: data,
          status: NOTIFICATION_STATUS.UNREAD,
          timestamp: new Date().toISOString(),
          sender: senderData
        };
        
        // Add to state
        state.notifications.unshift(notification);
        state.unreadCount += 1;
        
        console.log('Notification added, new count:', state.unreadCount);
      } catch (error) {
        console.error('Error creating connection rejected notification:', error);
      }
    },
    
    // Add processing for calendar share notifications
    addCalendarShareRequestNotification: (state, action) => {
      console.log("[Notification] addCalendarShareRequestNotification called with payload:", action.payload);
      
      try {
        // Helper function to get mock user data for testing
        const getMockUserData = (userId) => {
          // This simulates a database lookup for the user
          if (userId === "94edf0e2-985c-4cb6-a3b3-0df8ecd69c21") {
            return {
              id: "94edf0e2-985c-4cb6-a3b3-0df8ecd69c21",
              name: "Joys Debin",
              email: "startup.debinn@gmail.com",
              profile_photo_url: "https://d2tvks4ios06z3.cloudfront.net/users/94edf0e2-985c-4cb6-a3b3-0df8ecd69c21/profile/profile.jpg?v=43519009&t=1731692070"
            };
          }
          return null;
        };

        // Handle different data formats that might come from WebSocket
        let requestData;
        
        // Handle data format from the WebSocket component where we might get the full data object
        if (action.payload.type === 'CALENDAR_SHARE_REQUEST' || action.payload.type === 'calendar_share_request') {
          console.log("[Notification] Got direct notification format");
          requestData = action.payload;
        } 
        // Handle nested data format from socket.io
        else if (action.payload.data && action.payload.data.type === 'CALENDAR_SHARE_REQUEST') {
          console.log("[Notification] Got nested data format");
          requestData = action.payload.data;
        }
        // Handle original format where we have a 'request' property
        else if (action.payload.request) {
          console.log("[Notification] Got request property format");
          requestData = action.payload.request;
        } 
        // Try direct payload if other formats don't match
        else {
          console.log("[Notification] Using direct payload as fallback");
          requestData = action.payload;
        }
        
        console.log("[Notification] Processed request data:", requestData);
        
        // Ensure we have a valid request object
        if (!requestData) {
          console.error('[Notification] No valid data found for calendar share notification');
          return;
        }
        
        // Get the request ID, with fallbacks for different data formats
        const requestId = requestData.id || requestData._id || requestData.requestId || 
                         (requestData.event && requestData.event.id);
        
        if (!requestId) {
          console.error('[Notification] Calendar share request has no ID:', requestData);
          return;
        }
        
        // Create a notification ID
        const notificationId = `calendar-share-request-${requestId}`;
        
        // Check if this notification already exists
        const exists = state.notifications.some(n => n.id === notificationId);
        if (exists) {
          console.log('[Notification] Calendar share notification already exists:', notificationId);
          return;
        }
        
        // Extract sender information with comprehensive fallbacks
        // First try to extract a proper name from the request data
        let senderName = null;
        let senderId = null;
        
        console.log('[Notification] Looking for sender information in:', { 
          sender: requestData.sender, 
          senderName: requestData.senderName,
          requesterName: requestData.requesterName,
          user: requestData.user,
          senderId: requestData.senderId,
          requesterId: requestData.requesterId
        });

        // First check: if we have a sender ID, try to get user data from mock DB
        senderId = requestData.senderId || 
                  requestData.requesterId || 
                  (requestData.sender && requestData.sender.id) ||
                  (requestData.user && requestData.user.id);
        
        if (senderId) {
          const userData = getMockUserData(senderId);
          if (userData) {
            console.log('[Notification] Found user data in mock DB:', userData);
            senderName = userData.name;
            
            // Create a rich sender object
            requestData.sender = {
              ...requestData.sender,
              id: userData.id,
              name: userData.name,
              email: userData.email,
              profile_photo_url: userData.profile_photo_url
            };
            
            // Also update the data field
            if (!requestData.senderName) {
              requestData.senderName = userData.name;
            }
          }
        }
        
        // If user data lookup failed, continue with regular extraction
        if (!senderName) {
          // Try to extract the name from various fields
          if (requestData.sender && typeof requestData.sender === 'object') {
            const sender = requestData.sender;
            console.log('[Notification] Using sender object:', sender);
            
            if (sender.name) senderName = sender.name;
            else if (sender.fullName) senderName = sender.fullName;
            else if (sender.firstName && sender.lastName) senderName = `${sender.firstName} ${sender.lastName}`.trim();
            else if (sender.firstName) senderName = sender.firstName;
            else if (sender.username) senderName = sender.username;
            else if (sender.email) senderName = sender.email.split('@')[0];
          }
          
          // If we couldn't extract from sender object, try other fields
          if (!senderName) {
            senderName = requestData.senderName || 
                        requestData.requesterName || 
                        (requestData.user && requestData.user.name) ||
                        (requestData.user && requestData.user.username);
            
            console.log('[Notification] Using alternative sender name:', senderName);
          }
        }
        
        // If we have a sender ID but no name, create a formatted name from ID
        if (!senderName && senderId) {
          // Check once more with mock data
          if (senderId === "94edf0e2-985c-4cb6-a3b3-0df8ecd69c21") {
            senderName = "Joys Debin";
          } else {
            // Only use first 8 characters of the ID to make it more readable
            const shortId = typeof senderId === 'string' ? 
              senderId.substring(0, 8) : senderId.toString();
            
            senderName = `User ${shortId}`;
            console.log('[Notification] Created sender name from ID:', senderName);
          }
        }
        
        // Final fallback
        if (!senderName) {
          senderName = 'Unknown User';
          console.log('[Notification] Using default sender name');
        }
        
        const eventTitle = (requestData.event && requestData.event.title) || 
                          requestData.eventTitle || 
                          requestData.calendarName ||
                          'an event';
        
        console.log('[Notification] Creating new calendar share request notification');
        console.log('[Notification] Final Sender:', senderName);
        console.log('[Notification] Event:', eventTitle);
        
        // Create the notification object with rich sender data
        const notification = {
          id: notificationId,
          type: NOTIFICATION_TYPES.CALENDAR_SHARE_REQUEST,
          title: 'New Calendar Event Invitation',
          message: `${senderName} has shared "${eventTitle}" with you`,
          data: {
            ...requestData,
            senderName: senderName, // Ensure the sender name is included in the data
            eventTitle: eventTitle,  // Ensure the event title is included in the data
            user: requestData.user || null,
          },
          status: NOTIFICATION_STATUS.UNREAD,
          timestamp: requestData.timestamp || requestData.date || new Date().toISOString(),
          viewed: false,
          sender: {
            id: senderId,
            name: senderName,
            // Add additional fields that might be useful
            username: (requestData.sender && requestData.sender.username) || null,
            firstName: (requestData.sender && requestData.sender.firstName) || null,
            lastName: (requestData.sender && requestData.sender.lastName) || null,
            email: (requestData.sender && requestData.sender.email) || null,
            profile_photo_url: (requestData.sender && requestData.sender.profile_photo_url) || null
          }
        };
        
        console.log('[Notification] Created notification object:', notification);
        
        // Add to state
        state.notifications.unshift(notification);
        state.unreadCount += 1;
        
        console.log('[Notification] Calendar share notification added, new count:', state.unreadCount);
      } catch (error) {
        console.error('[Notification] Error creating calendar share notification:', error);
      }
    },
    
    // Add processing for calendar share notifications
    addCalendarShareAcceptedNotification: (state, action) => {
      console.log("addCalendarShareAcceptedNotification called");
      
      try {
        const { data } = action.payload;
        
        // Ensure we have valid data
        if (!data) {
          console.error('No data provided for calendar share accepted notification');
          return;
        }
        
        // Get IDs
        const requestId = data.requestId;
        const eventId = data.eventId;
        if (!requestId || !eventId) {
          console.error('Missing requestId or eventId:', data);
          return;
        }
        
        // Create a notification ID
        const notificationId = `calendar-share-accepted-${requestId}-${Date.now()}`;
        
        // Extract recipient information
        const recipientName = data.recipientName || 'Someone';
        const eventTitle = data.eventTitle || 'your event';
        
        console.log('Creating new calendar share accepted notification from:', recipientName);
        
        // Create the notification object
        const notification = {
          id: notificationId,
          type: NOTIFICATION_TYPES.CALENDAR_SHARE_ACCEPTED,
          title: 'Calendar Event Invitation Accepted',
          message: `${recipientName} accepted your invitation to "${eventTitle}"`,
          data: data,
          status: NOTIFICATION_STATUS.UNREAD,
          timestamp: new Date().toISOString(),
          sender: {
            id: data.recipientId,
            name: recipientName
          }
        };
        
        // Add to state
        state.notifications.unshift(notification);
        state.unreadCount += 1;
        
        console.log('Calendar share accepted notification added, new count:', state.unreadCount);
      } catch (error) {
        console.error('Error creating calendar share accepted notification:', error);
      }
    },
    
    // Add processing for calendar share notifications
    addCalendarShareRejectedNotification: (state, action) => {
      console.log("addCalendarShareRejectedNotification called");
      
      try {
        const { data } = action.payload;
        
        // Ensure we have valid data
        if (!data) {
          console.error('No data provided for calendar share rejected notification');
          return;
        }
        
        // Get IDs
        const requestId = data.requestId;
        const eventId = data.eventId;
        if (!requestId || !eventId) {
          console.error('Missing requestId or eventId:', data);
          return;
        }
        
        // Create a notification ID
        const notificationId = `calendar-share-rejected-${requestId}-${Date.now()}`;
        
        // Extract recipient information
        const recipientName = data.recipientName || 'Someone';
        const eventTitle = data.eventTitle || 'your event';
        
        console.log('Creating new calendar share rejected notification from:', recipientName);
        
        // Create the notification object
        const notification = {
          id: notificationId,
          type: NOTIFICATION_TYPES.CALENDAR_SHARE_REJECTED,
          title: 'Calendar Event Invitation Declined',
          message: `${recipientName} declined your invitation to "${eventTitle}"`,
          data: data,
          status: NOTIFICATION_STATUS.UNREAD,
          timestamp: new Date().toISOString(),
          sender: {
            id: data.recipientId,
            name: recipientName
          }
        };
        
        // Add to state
        state.notifications.unshift(notification);
        state.unreadCount += 1;
        
        console.log('Calendar share rejected notification added, new count:', state.unreadCount);
      } catch (error) {
        console.error('Error creating calendar share rejected notification:', error);
      }
    },
    
    markNotificationViewed: (state, action) => {
      const notificationId = action.payload;
      const notification = state.notifications.find(n => n.id === notificationId);
      if (notification) {
        notification.viewed = true;
        notification.status = NOTIFICATION_STATUS.READ;
        state.unreadCount = state.notifications.filter(n => n.status === NOTIFICATION_STATUS.UNREAD).length;
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchNotifications
      .addCase(fetchNotifications.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.isLoading = false;
        state.notifications = action.payload;
        state.unreadCount = action.payload.filter(n => n.status === NOTIFICATION_STATUS.UNREAD).length;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      
      // Handle markNotificationAsRead
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        const notification = state.notifications.find(n => n.id === action.payload.notificationId);
        if (notification && notification.status === NOTIFICATION_STATUS.UNREAD) {
          notification.status = NOTIFICATION_STATUS.READ;
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      });
  }
});

// Export actions
export const { 
  addNotification, 
  markAsRead, 
  markAllAsRead, 
  removeNotification, 
  clearNotifications,
  addConnectionRequestNotification,
  addConnectionAcceptedNotification,
  addConnectionRejectedNotification,
  addCalendarShareRequestNotification,
  addCalendarShareAcceptedNotification,
  addCalendarShareRejectedNotification,
  markNotificationViewed
} = notificationSlice.actions;

// Export selectors
export const selectNotifications = state => state.notifications.notifications;
export const selectUnreadCount = state => state.notifications.unreadCount;
export const selectIsLoading = state => state.notifications.isLoading;
export const selectError = state => state.notifications.error;

// Export reducer
export default notificationSlice.reducer; 